/** @fileoverview Describes which files can be attached, their client-side limits, and error text. */

import { ApiError } from '../services/platformAuth';
import type {
  AttachmentKind,
  ConversationFilesResponse,
  ProcessFailureReason,
} from '../types/attachments';
import { ACCEPTED_IMAGE_TYPES } from './imageAttachments';

export const MAX_ATTACHMENTS_PER_MESSAGE = 5;
export const MAX_DOCUMENT_BYTES = 5 * 1024 * 1024;
export const MAX_PDF_PAGES = 40;
/** A document above this many tokens is only reachable through search, so Send waits for indexing. */
export const WHOLE_FILE_TOKEN_LIMIT = 25_000;

const DOCUMENT_EXTENSIONS = [
  '.pdf',
  '.csv',
  '.tsv',
  '.txt',
  '.md',
  '.log',
  '.json',
  '.html',
  '.htm',
];
/** Value for the file input `accept` attribute. */
export const ACCEPT_ATTRIBUTE = [...ACCEPTED_IMAGE_TYPES, ...DOCUMENT_EXTENSIONS].join(',');

const extensionOf = (name: string) => name.slice(name.lastIndexOf('.')).toLowerCase();

/** "3 pages" for a PDF. Other files have no real pages, so nothing is shown for them. */
export const pageLabel = (name: string, pages?: number) =>
  pages && extensionOf(name) === '.pdf' ? `${pages} ${pages === 1 ? 'page' : 'pages'}` : undefined;

/** Files that did not fit the budget; the assistant can only search them. */
export const omittedCount = (data?: ConversationFilesResponse) =>
  data?.attachments.filter((file) => file.placement === 'omitted').length ?? 0;

/** Returns the kind of a file, or undefined when its type is not accepted. */
export const attachmentKind = (file: File): AttachmentKind | undefined => {
  if (ACCEPTED_IMAGE_TYPES.includes(file.type)) return 'image';
  return DOCUMENT_EXTENSIONS.includes(extensionOf(file.name)) ? 'document' : undefined;
};

const DOCUMENT_MEDIA_TYPES: Record<string, string> = {
  '.pdf': 'application/pdf',
  '.csv': 'text/csv',
  '.tsv': 'text/tab-separated-values',
  '.txt': 'text/plain',
  '.log': 'text/plain',
  '.md': 'text/markdown',
  '.json': 'application/json',
  '.html': 'text/plain',
  '.htm': 'text/plain',
};

/** The media type the server expects for a document, from its extension: browsers often guess wrong. */
export const documentMediaType = (file: File) =>
  DOCUMENT_MEDIA_TYPES[extensionOf(file.name)] ?? file.type;

/** Returns why a document cannot be attached, or undefined when it is fine. Images are shrunk instead. */
export const documentProblem = (file: File) =>
  file.size > MAX_DOCUMENT_BYTES
    ? `${file.name} is over ${MAX_DOCUMENT_BYTES / 1024 / 1024} MB.`
    : undefined;

const FAILURE_TEXT: Record<ProcessFailureReason, string> = {
  EMPTY: 'This file has no readable content.',
  TOO_MANY_PAGES: `This file has too many pages (${MAX_PDF_PAGES} at most).`,
  TOO_MANY_ROWS: 'This file has too many rows.',
  UNREADABLE: 'This file could not be read.',
  UNSUPPORTED_TYPE: 'This file type is not supported.',
  TOO_LARGE: 'This file is too large.',
  NOT_UPLOADED: 'The upload did not finish. Try again.',
};

/** Reasons a retry cannot fix: the user must remove the file. */
export const isFinalFailure = (reason: ProcessFailureReason) =>
  reason === 'UNSUPPORTED_TYPE' || reason === 'TOO_LARGE';

/** Text for a process failure reported by the server. */
export const processFailureMessage = (reason: ProcessFailureReason) =>
  FAILURE_TEXT[reason] ?? 'The file could not be processed. Try again.';

/** Turns a failed upload or process call into a message for the user. */
export function attachmentErrorMessage(error: unknown) {
  if (error instanceof ApiError) {
    // The server sends a localized sentence for 4xx errors, which ApiError carries as its message.
    if (error.status === 404) return 'This file or conversation is no longer available.';
    if (error.status === 429) return error.message;
    if (error.status && error.status < 500 && error.status !== 401) return error.message;
  }
  return 'The file could not be processed. Try again.';
}
